<template>
  <div class="jce">
    <div class="jce__head">
      <b-form-checkbox :checked="enabled" switch @change="toggle">
        <span class="jce__title"><feather-icon icon="ZapIcon" size="13"/> {{ msg('showonlyif') }}<info-hint :text="msg('info_showonlyif')"/></span>
      </b-form-checkbox>
    </div>

    <div v-if="enabled" class="jce__body">
      <v-select
          append-to-body
          class="jce__field"
          :value="condition.questionKey"
          :options="availableQuestions"
          label="label"
          :reduce="q => q.key"
          :clearable="false"
          :placeholder="msg('question')"
          :dir="isRTL ? 'rtl' : 'ltr'"
          @input="patch({questionKey: $event, value: null})"
      />
      <v-select
          append-to-body
          class="jce__field jce__field--op"
          :value="condition.operator"
          :options="operators"
          label="text"
          :reduce="o => o.value"
          :clearable="false"
          :dir="isRTL ? 'rtl' : 'ltr'"
          @input="patch({operator: $event})"
      />
      <template v-if="!isPresenceCheck">
        <!-- ordering compares times/dates/numbers — always a free value, never the option list -->
        <b-form-input v-if="isOrdering" class="jce__field" :value="condition.value"
                      placeholder="15:00 / {checkInTime}" @input="patch({value: $event})"/>
        <v-select
            v-else-if="targetOptions.length"
            append-to-body
            class="jce__field"
            :value="condition.value"
            :options="targetOptions"
            label="label"
            :reduce="o => o.value"
            :clearable="false"
            :dir="isRTL ? 'rtl' : 'ltr'"
            @input="patch({value: $event})"
        />
        <b-form-input v-else class="jce__field" :value="condition.value"
                      :placeholder="msg('value')" @input="patch({value: $event})"/>
      </template>
    </div>

    <small v-if="enabled && isOrdering" class="d-block mt-25 text-muted">{{ msg('timetokenhint') }}</small>
    <small v-if="enabled && !condition.questionKey" class="text-warning">{{ msg('conditionpickquestion') }}</small>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {CONDITION_OPERATORS} from '@/model/journey/journeyTypes'
import InfoHint from './InfoHint.vue'

/**
 * "Show this only if ..." editor. Only questions that come BEFORE the edited
 * node are offered, so a condition can never depend on a later answer.
 */
export default {
  name: 'journeyConditionEditor',
  components: {InfoHint},
  props: {
    value: {type: Object, default: null},
    /** [{key, label}] of questions preceding this node */
    availableQuestions: {type: Array, default: () => []},
    /** all questions, to resolve the option list of the referenced one */
    allQuestions: {type: Array, default: () => []},
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('appConfig', ['isRTL']),
    condition() {
      return this.value || {};
    },
    enabled() {
      return this.value != null;
    },
    operators() {
      return CONDITION_OPERATORS.map(operator => ({...operator, text: this.msg(operator.label)}));
    },
    isPresenceCheck() {
      return ['ANSWERED', 'NOT_ANSWERED'].includes(this.condition.operator);
    },
    isOrdering() {
      return ['BEFORE', 'AFTER'].includes(this.condition.operator);
    },
    targetOptions() {
      const source = this.allQuestions.find(question => question.key === this.condition.questionKey);
      if (!source) return [];
      if (source.type === 'YES_NO' || source.type === 'CONSENT')
        return [{value: 'true', label: this.msg('Yes')}, {value: 'false', label: this.msg('No')}];
      return (source.options || []).map(option => ({value: option.value, label: option.label}));
    },
  },
  methods: {
    toggle(enabled) {
      this.$emit('input', enabled
          ? {questionKey: null, operator: 'EQUALS', value: null}
          : null);
    },
    patch(patch) {
      this.$emit('input', {...this.condition, ...patch});
    },
  }
}
</script>

<style lang="scss" scoped>
.jce {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 159, 67, .07);
  border: 1px solid rgba(255, 159, 67, .25);

  &__title {
    font-weight: 600;
    color: #ff9f43;
  }

  &__body {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  &__field {
    flex: 1 1 150px;
    min-width: 130px;

    &--op {
      flex: 0 1 160px;
    }
  }
}
</style>
